"use client"

import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { ChevronDown, ChevronUp } from "lucide-react"
import { cn } from "@/lib/utils"
import { Skeleton } from "@/components/ui/skeleton"
import { getScores } from "@/lib/api/scores"
import type { BacktestWatchlistItem } from "@/types/backtest"
import type { ZScoreRawValues } from "@/types/score"

// ── 포맷 헬퍼 ────────────────────────────────────────────────────────────────

function fmtWon(n: number): string {
  const eok = n / 100_000_000
  const abs  = Math.abs(eok)
  if (abs >= 10_000) return `${(eok / 10_000).toFixed(1)}조`
  if (abs >= 1)      return `${Math.round(eok).toLocaleString("ko-KR")}억`
  return `${Math.round(n / 1_000_000).toLocaleString("ko-KR")}백만`
}

function fmtEok(n: number): string {
  if (n >= 10_000) return `${(n / 10_000).toFixed(1)}조`
  return `${n.toLocaleString("ko-KR")}억`
}

// ── 상수 ─────────────────────────────────────────────────────────────────────

const GAUGE_MAX = 4

const GRADE_BADGE: Record<string, string> = {
  "안전":      "bg-emerald-500/20 text-emerald-400 border-emerald-500/40",
  "주의":      "bg-yellow-500/20  text-yellow-400  border-yellow-500/40",
  "위험":      "bg-red-500/20     text-red-400     border-red-500/40",
  "데이터부족": "bg-zinc-700/60   text-zinc-400    border-zinc-600",
}

// 게이지 구간 너비 (GAUGE_MAX = 4 기준 %)
const DANGER_W  = (1.81 / GAUGE_MAX) * 100          // 0 ~ 1.81
const CAUTION_W = ((2.99 - 1.81) / GAUGE_MAX) * 100 // 1.81 ~ 2.99
// SAFE_W = 나머지 flex-1

const COMPONENTS = [
  { key: "x1" as const, label: "X1", desc: "운전자본 / 총자산",   weight: 1.2 },
  { key: "x2" as const, label: "X2", desc: "이익잉여금 / 총자산", weight: 1.4 },
  { key: "x3" as const, label: "X3", desc: "영업이익 / 총자산",   weight: 3.3 },
  { key: "x4" as const, label: "X4", desc: "시가총액 / 총부채",   weight: 0.6 },
  { key: "x5" as const, label: "X5", desc: "매출액 / 총자산",     weight: 1.0 },
]

// M-Score 게이지 구간 (-3 ~ 0 기준 %)
const M_GAUGE_MIN = -3
const M_GAUGE_MAX = 0
const M_SAFE_END   = -2.22
const M_CAUTION_END = -1.78
const mPct = (v: number) => ((v - M_GAUGE_MIN) / (M_GAUGE_MAX - M_GAUGE_MIN)) * 100

const M_SAFE_W    = mPct(M_SAFE_END)                 // -3 ~ -2.22
const M_CAUTION_W = mPct(M_CAUTION_END) - mPct(M_SAFE_END) // -2.22 ~ -1.78
// M_DANGER_W = 나머지 flex-1

const MSCORE_GRADE_BADGE: Record<string, string> = {
  "안전":      "bg-emerald-500/20 text-emerald-400 border-emerald-500/40",
  "주의":      "bg-yellow-500/20  text-yellow-400  border-yellow-500/40",
  "위험":      "bg-red-500/20     text-red-400     border-red-500/40",
  "데이터부족": "bg-zinc-700/60   text-zinc-400    border-zinc-600",
}

const M_COMPONENTS = [
  { key: "dsri" as const, label: "DSRI", desc: "매출채권 증가율",   weight: 0.920 },
  { key: "gmi"  as const, label: "GMI",  desc: "매출총이익률 변화", weight: 0.528 },
  { key: "aqi"  as const, label: "AQI",  desc: "자산건전성 변화",   weight: 0.404 },
  { key: "sgi"  as const, label: "SGI",  desc: "매출 성장률",       weight: 0.892 },
  { key: "sgai" as const, label: "SGAI", desc: "판관비율 변화",     weight: -0.172 },
  { key: "tata" as const, label: "TATA", desc: "총발생액/총자산",   weight: 4.679 },
  { key: "lvgi" as const, label: "LVGI", desc: "부채비율 변화",     weight: -0.327 },
]

const RAW_ROWS: { key: keyof ZScoreRawValues; label: string; isEok?: boolean }[] = [
  { key: "currentAssets",      label: "유동자산"  },
  { key: "currentLiabilities", label: "유동부채"  },
  { key: "totalAssets",        label: "자산총계"  },
  { key: "retainedEarnings",   label: "이익잉여금" },
  { key: "operatingIncome",    label: "영업이익"  },
  { key: "totalLiabilities",   label: "부채총계"  },
  { key: "revenue",            label: "매출액"    },
  { key: "marketCap",          label: "시가총액",  isEok: true },
]

// ── 컴포넌트 ─────────────────────────────────────────────────────────────────

interface Props {
  selected: BacktestWatchlistItem | null
}

export function StockVerificationTab({ selected }: Props) {
  const [rawOpen, setRawOpen] = useState(false)

  const { data, isLoading, isError } = useQuery({
    queryKey: ["scores", selected?.stockCode],
    queryFn:  () => getScores(selected!.stockCode),
    enabled:  !!selected,
  })

  if (!selected) {
    return (
      <div className="flex h-24 items-center justify-center text-xs text-zinc-500">
        종목을 선택하세요.
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="space-y-3 p-4">
        <Skeleton className="h-10 w-40 bg-zinc-800" />
        <Skeleton className="h-5 w-full bg-zinc-800" />
        <Skeleton className="h-36 w-full bg-zinc-800" />
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="flex h-24 items-center justify-center text-xs text-red-400">
        데이터를 불러올 수 없습니다.
      </div>
    )
  }

  const { zScore, mScore } = data
  const { score, grade, basedOnYear, fsDiv, components, rawValues } = zScore
  const gaugeWidth = score != null ? Math.min((score / GAUGE_MAX) * 100, 100) : 0

  const mGaugeWidth = mScore.score != null
    ? Math.min(Math.max(mPct(mScore.score), 0), 100)
    : 0

  return (
    <div className="space-y-4 p-4">

      {/* ── 점수 + 등급 ── */}
      <div className="flex items-end gap-3">
        <span className="font-mono text-3xl font-bold text-zinc-100">
          {score != null ? score.toFixed(2) : "-"}
        </span>
        <span className={cn(
          "mb-0.5 rounded border px-2 py-0.5 text-xs font-semibold",
          GRADE_BADGE[grade] ?? GRADE_BADGE["데이터부족"]
        )}>
          {grade}
        </span>
        {basedOnYear && (
          <span className="mb-0.5 text-xs text-zinc-500">
            {basedOnYear}년 기준 · {fsDiv}
          </span>
        )}
      </div>

      {/* ── 게이지 바 ── */}
      <div>
        {/* 3구간 배경 + 마커 오버레이 */}
        <div className="relative h-3 w-full overflow-hidden rounded-full flex">
          <div className="h-full bg-red-500/50"     style={{ width: `${DANGER_W}%` }} />
          <div className="h-full bg-yellow-500/50"  style={{ width: `${CAUTION_W}%` }} />
          <div className="h-full flex-1 bg-emerald-500/50" />
          {/* 현재 점수 위치 흰색 라인 */}
          {score != null && (
            <div
              className="absolute inset-y-0 w-0.5 bg-white/90"
              style={{ left: `${gaugeWidth}%` }}
            />
          )}
        </div>
        {/* ▼ 마커 */}
        <div className="relative h-3">
          {score != null && (
            <span
              className="-translate-x-1/2 absolute top-0 text-[9px] leading-none text-white"
              style={{ left: `${gaugeWidth}%` }}
            >▼</span>
          )}
        </div>
        {/* 눈금 라벨 */}
        <div className="relative h-3">
          <span className="absolute left-0 text-[10px] text-zinc-600">0</span>
          <span
            className="-translate-x-1/2 absolute text-[10px] text-yellow-500/80"
            style={{ left: `${DANGER_W}%` }}
          >1.81</span>
          <span
            className="-translate-x-1/2 absolute text-[10px] text-emerald-500/80"
            style={{ left: `${(DANGER_W + CAUTION_W)}%` }}
          >2.99</span>
          <span className="absolute right-0 text-[10px] text-zinc-600">4</span>
        </div>
      </div>

      {/* ── 구성 지표 테이블 ── */}
      {components ? (
        <div>
          <p className="mb-2 text-xs font-medium text-zinc-400">구성 지표</p>
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-zinc-800 text-left text-zinc-500">
                <th className="pb-1.5 font-normal">변수</th>
                <th className="pb-1.5 font-normal">설명</th>
                <th className="pb-1.5 text-right font-normal font-mono">값</th>
                <th className="pb-1.5 text-right font-normal">가중치</th>
                <th className="pb-1.5 text-right font-normal font-mono">기여도</th>
              </tr>
            </thead>
            <tbody>
              {COMPONENTS.map(({ key, label, desc, weight }) => {
                const val          = components[key]
                const contribution = val * weight
                return (
                  <tr key={key} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                    <td className="py-1.5 font-mono text-zinc-300">{label}</td>
                    <td className="py-1.5 text-zinc-500">{desc}</td>
                    <td className="py-1.5 text-right font-mono text-zinc-300">{val.toFixed(3)}</td>
                    <td className="py-1.5 text-right text-zinc-500">×{weight.toFixed(1)}</td>
                    <td className={cn(
                      "py-1.5 text-right font-mono",
                      contribution >= 0 ? "text-red-400" : "text-blue-400"
                    )}>
                      {contribution.toFixed(3)}
                    </td>
                  </tr>
                )
              })}
              <tr className="border-t border-zinc-700">
                <td colSpan={4} className="py-1.5 text-right text-zinc-400">Z-Score 합계</td>
                <td className="py-1.5 text-right font-mono font-semibold text-zinc-100">
                  {score?.toFixed(4) ?? "-"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-xs text-zinc-500">구성 지표 데이터 없음</p>
      )}

      {/* ── 원본 수치 (접기/펼치기) ── */}
      {rawValues && (
        <div>
          <button
            onClick={() => setRawOpen(!rawOpen)}
            className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300"
          >
            {rawOpen ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
            원본 수치
          </button>
          {rawOpen && (
            <div className="mt-2 grid grid-cols-2 gap-x-8 gap-y-1 rounded-lg bg-zinc-800/40 p-3 text-xs">
              {RAW_ROWS.map(({ key, label, isEok }) => {
                const value = rawValues[key]
                const formatted = isEok ? fmtEok(value) : fmtWon(value)
                return (
                  <div key={key} className="flex justify-between">
                    <span className="text-zinc-500">{label}</span>
                    <span className={cn(
                      "font-mono",
                      value < 0 ? "text-blue-400" : "text-zinc-300"
                    )}>
                      {formatted}
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ── 판단 기준 범례 ── */}
      <div className="rounded-lg bg-zinc-800/40 p-3">
        <p className="mb-2 text-[10px] font-medium text-zinc-500">Altman Z-Score 판단 기준</p>
        <div className="space-y-1 text-[10px]">
          <div className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
            <span className="text-zinc-400">Z &gt; 2.99 — 안전 (재무건전)</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-yellow-500" />
            <span className="text-zinc-400">1.81 ~ 2.99 — 주의 (회색지대)</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" />
            <span className="text-zinc-400">Z &lt; 1.81 — 위험 (부실위험)</span>
          </div>
        </div>
      </div>

      {/* ── Z-Score / M-Score 구분선 ── */}
      <div className="border-t border-zinc-800" />

      {/* ── M-Score: 점수 + 등급 ── */}
      <div className="flex items-end gap-3">
        <span className="font-mono text-3xl font-bold text-zinc-100">
          {mScore.score != null ? mScore.score.toFixed(2) : "-"}
        </span>
        <span className={cn(
          "mb-0.5 rounded border px-2 py-0.5 text-xs font-semibold",
          MSCORE_GRADE_BADGE[mScore.grade] ?? MSCORE_GRADE_BADGE["데이터부족"]
        )}>
          {mScore.grade}
        </span>
        {mScore.basedOnYear && (
          <span className="mb-0.5 text-xs text-zinc-500">
            {mScore.basedOnYear}년 기준 · {mScore.fsDiv} · DEPI 제외
          </span>
        )}
      </div>

      {/* ── M-Score: 게이지 바 ── */}
      <div>
        <div className="relative h-3 w-full overflow-hidden rounded-full flex">
          <div className="h-full bg-emerald-500/50" style={{ width: `${M_SAFE_W}%` }} />
          <div className="h-full bg-yellow-500/50"  style={{ width: `${M_CAUTION_W}%` }} />
          <div className="h-full flex-1 bg-red-500/50" />
          {mScore.score != null && (
            <div
              className="absolute inset-y-0 w-0.5 bg-white/90"
              style={{ left: `${mGaugeWidth}%` }}
            />
          )}
        </div>
        <div className="relative h-3">
          {mScore.score != null && (
            <span
              className="-translate-x-1/2 absolute top-0 text-[9px] leading-none text-white"
              style={{ left: `${mGaugeWidth}%` }}
            >▼</span>
          )}
        </div>
        <div className="relative h-3">
          <span className="absolute left-0 text-[10px] text-zinc-600">-3</span>
          <span
            className="-translate-x-1/2 absolute text-[10px] text-emerald-500/80"
            style={{ left: `${M_SAFE_W}%` }}
          >-2.22</span>
          <span
            className="-translate-x-1/2 absolute text-[10px] text-yellow-500/80"
            style={{ left: `${M_SAFE_W + M_CAUTION_W}%` }}
          >-1.78</span>
          <span className="absolute right-0 text-[10px] text-zinc-600">0</span>
        </div>
      </div>

      {/* ── M-Score: 구성 지표 테이블 ── */}
      {mScore.components ? (
        <div>
          <p className="mb-2 text-xs font-medium text-zinc-400">구성 지표</p>
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-zinc-800 text-left text-zinc-500">
                <th className="pb-1.5 font-normal">변수</th>
                <th className="pb-1.5 font-normal">설명</th>
                <th className="pb-1.5 text-right font-normal font-mono">값</th>
                <th className="pb-1.5 text-right font-normal">가중치</th>
                <th className="pb-1.5 text-right font-normal font-mono">기여도</th>
              </tr>
            </thead>
            <tbody>
              {M_COMPONENTS.map(({ key, label, desc, weight }) => {
                const val          = mScore.components![key]
                const contribution = val != null ? val * weight : null
                return (
                  <tr key={key} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                    <td className="py-1.5 font-mono text-zinc-300">{label}</td>
                    <td className="py-1.5 text-zinc-500">{desc}</td>
                    <td className="py-1.5 text-right font-mono text-zinc-300">
                      {val != null ? val.toFixed(3) : "-"}
                    </td>
                    <td className="py-1.5 text-right text-zinc-500">×{weight.toFixed(3)}</td>
                    <td className={cn(
                      "py-1.5 text-right font-mono",
                      contribution == null ? "text-zinc-500"
                        : contribution >= 0 ? "text-red-400" : "text-blue-400"
                    )}>
                      {contribution != null ? contribution.toFixed(3) : "-"}
                    </td>
                  </tr>
                )
              })}
              <tr className="border-t border-zinc-700">
                <td colSpan={4} className="py-1.5 text-right text-zinc-400">M-Score 합계</td>
                <td className="py-1.5 text-right font-mono font-semibold text-zinc-100">
                  {mScore.score?.toFixed(4) ?? "-"}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-xs text-zinc-500">구성 지표 데이터 없음</p>
      )}

      {/* ── M-Score: 판단 기준 범례 ── */}
      <div className="rounded-lg bg-zinc-800/40 p-3">
        <p className="mb-2 text-[10px] font-medium text-zinc-500">Beneish M-Score 판단 기준</p>
        <div className="space-y-1 text-[10px]">
          <div className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
            <span className="text-zinc-400">M &lt; -2.22 — 안전 (분식 가능성 낮음)</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-yellow-500" />
            <span className="text-zinc-400">-2.22 ~ -1.78 — 주의</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-red-500" />
            <span className="text-zinc-400">M &gt; -1.78 — 위험 (분식 가능성 높음)</span>
          </div>
        </div>
        <p className="mt-2 text-[10px] text-zinc-600">
          ※ Beneish M-Score, DEPI 항목 제외 (감가상각비 미수집)
        </p>
      </div>

    </div>
  )
}
